import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  FileText,
  Paperclip,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { getContacts } from '../api/contacts';
import { getProperties } from '../api/properties';
import { listAllTenants } from '../api/tenants';
import { createTask, deleteTask, getTasks, updateTask } from '../api/tasks';
import {
  assignJobContractor,
  assignJobWorker,
  createJob,
  getJobs,
  openJobAttachment,
  reviewJobProof,
  reviewJobQuote,
  scheduleJob,
  updateJobQuoteRequirement,
} from '../api/jobs';
import { useSubscription } from '../hooks/useSubscription';
import { useSubmissionLock } from '../hooks/useSubmissionLock';
import { createIdempotencyKey } from '../lib/idempotency';
import { formatDateTime, formatStatus } from '../lib/formatters';

const CONTRACTOR_TERMS = [
  'maintenance',
  'contractor',
  'tradesperson',
  'trade',
  'worker',
  'electrician',
  'plumber',
  'cleaner',
  'builder',
  'decorator',
];

const JOB_TYPES = [
  'MAINTENANCE',
  'INSPECTION',
  'INVENTORY',
  'GAS_SAFETY',
  'ELECTRICAL_INSPECTION',
  'CLEANING',
  'DECORATING',
  'EPC',
];

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'EMERGENCY'];
const ACCESS_REQUIREMENTS = ['TENANT_REQUIRED', 'LANDLORD_REQUIRED', 'NO_INTERNAL_ACCESS'];

function emptyJobForm(propertyId = '') {
  return {
    propertyId,
    title: '',
    description: '',
    jobType: 'MAINTENANCE',
    priority: 'MEDIUM',
    accessRequirement: 'LANDLORD_REQUIRED',
    tenantId: '',
    contractorId: '',
    tenantAvailability: '',
    accessNotes: '',
    internalNotes: '',
    notifyTenant: false,
    attachments: [],
  };
}

function emptyTaskForm(propertyId = '') {
  return {
    title: '',
    description: '',
    dueDate: '',
    propertyId,
    contactId: '',
  };
}

function isImageAttachment(attachment) {
  const mimeType = String(attachment?.mimeType || '').toLowerCase();
  const filename = String(attachment?.filename || '').toLowerCase();
  return mimeType.startsWith('image/') || /\.(png|jpe?g|webp|gif|avif)$/.test(filename);
}

function completionPhotoAttachments(job) {
  return (job?.completion?.proofAttachments || []).filter(isImageAttachment);
}

function contactDisplayName(contact) {
  const fullName = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim();
  return fullName || contact?.name || contact?.company || contact?.email || contact?.phone || 'Contact';
}

function hasValidPhone(contact) {
  const digits = String(contact?.phone || '').replace(/\D/g, '');
  return digits.length >= 7;
}

function isContractorContact(contact) {
  const values = [
    contact?.relationship,
    contact?.category,
    contact?.type,
    contact?.role,
    contact?.notes,
    contact?.company,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return CONTRACTOR_TERMS.some((term) => values.includes(term));
}

function propertyLabel(property) {
  return property?.title || property?.address || [property?.address1, property?.town, property?.postcode].filter(Boolean).join(', ') || 'Property';
}

function sourceLabel(job) {
  if (job?.isLegacyRequest || job?.source === 'TENANT_REQUEST' || job?.requestId) return 'Tenant request';
  return 'Landlord created';
}

function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function timeInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toTimeString().slice(0, 5);
}

function buildLocalDateTime(date, time) {
  return date && time ? `${date}T${time}` : '';
}

function isTaskOverdue(task) {
  if (!task?.dueDate || task.completed) return false;
  const due = new Date(task.dueDate);
  if (Number.isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

function taskSortValue(task) {
  if (task?.dueDate) {
    const due = new Date(task.dueDate).getTime();
    if (Number.isFinite(due)) return due;
  }
  const updated = new Date(task?.updatedAt || task?.createdAt || 0).getTime();
  return Number.isFinite(updated) ? updated : 0;
}

function statusClass(status) {
  if (['CLOSED', 'COMPLETED'].includes(status)) return 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30';
  if (['WORKER_DECLINED', 'QUOTE_DECLINED', 'NEEDS_REASSIGNMENT', 'FOLLOW_UP_REQUIRED', 'APPOINTMENT_REJECTED', 'CANCELLED'].includes(status)) {
    return 'bg-red-500/20 text-red-200 border-red-400/30';
  }
  if (['QUOTE_SUBMITTED', 'PROOF_SUBMITTED', 'COMPLETION_SUBMITTED', 'DATE_PROPOSED', 'AWAITING_LANDLORD_CONFIRMATION', 'AWAITING_TENANT_CONFIRMATION'].includes(status)) {
    return 'bg-amber-500/20 text-amber-100 border-amber-400/30';
  }
  return 'bg-white/10 text-white/80 border-white/10';
}

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialPropertyId = searchParams.get('propertyId') || '';
  const highlightedJobId = searchParams.get('jobId') || '';
  const createParam = searchParams.get('create') || '';
  const createJobRequested = createParam === '1' || createParam === 'job';
  const createTaskRequested = createParam === 'task';

  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const jobsUnlocked = !subscriptionLoading && hasFeature('jobs');

  const [viewFilter, setViewFilter] = useState(() => {
    const requested = searchParams.get('type');
    return ['all', 'tasks', 'jobs'].includes(requested) ? requested : 'all';
  });
  const [propertyFilter, setPropertyFilter] = useState(initialPropertyId);

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState('');
  const [showTaskForm, setShowTaskForm] = useState(createTaskRequested);
  const [taskForm, setTaskForm] = useState(() => emptyTaskForm(initialPropertyId));
  const [taskFormError, setTaskFormError] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [savingTask, setSavingTask] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLists, setLoadingLists] = useState(true);
  const [error, setError] = useState('');
  const [createError, setCreateError] = useState('');
  const [createNotice, setCreateNotice] = useState('');
  const [showCreate, setShowCreate] = useState(createJobRequested);
  const [form, setForm] = useState(() => emptyJobForm(initialPropertyId));
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const { run: runSubmission, isPending: isActionPending } = useSubmissionLock();

  const [assigningJob, setAssigningJob] = useState(null);
  const [selectedContractorId, setSelectedContractorId] = useState('');
  const [assigningJobId, setAssigningJobId] = useState(null);
  const [sendingSmsJobId, setSendingSmsJobId] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [activeActionLabels, setActiveActionLabels] = useState({});
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState({});
  const [imageViewer, setImageViewer] = useState(null);

  useEffect(() => {
    setPropertyFilter(initialPropertyId);
    if (initialPropertyId) {
      setForm((current) => ({ ...current, propertyId: initialPropertyId }));
      setTaskForm((current) => ({ ...current, propertyId: initialPropertyId }));
    }
    if (createJobRequested) setShowCreate(true);
    if (createTaskRequested) setShowTaskForm(true);
  }, [createJobRequested, createTaskRequested, initialPropertyId]);

  useEffect(() => {
    const requested = searchParams.get('type');
    if (['all', 'tasks', 'jobs'].includes(requested)) {
      setViewFilter(requested);
    }
  }, [searchParams]);

  useEffect(() => {
    if (highlightedJobId && !initialPropertyId) {
      setPropertyFilter('');
    }
  }, [highlightedJobId, initialPropertyId]);

  useEffect(() => {
    let mounted = true;
    setTasksLoading(true);
    setTasksError('');

    getTasks()
      .then((res) => {
        const data = res?.data;
        if (mounted) setTasks(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching tasks:', err);
        if (mounted) setTasksError('Unable to load tasks.');
      })
      .finally(() => {
        if (mounted) setTasksLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (subscriptionLoading) return;
    if (!jobsUnlocked) {
      setJobs([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError('');

    getJobs()
      .then((data) => {
        if (mounted) setJobs(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching jobs:', err);
        if (mounted) setError('Unable to load jobs.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [jobsUnlocked, subscriptionLoading]);

  useEffect(() => {
    if (!jobsUnlocked || jobs.length === 0) return;

    const missing = [];
    jobs.forEach((job) => {
      completionPhotoAttachments(job).forEach((attachment) => {
        if (!Object.prototype.hasOwnProperty.call(attachmentPreviewUrls, attachment.id)) {
          missing.push({ jobId: job.id, attachmentId: attachment.id });
        }
      });
    });

    if (missing.length === 0) return;

    let cancelled = false;

    Promise.all(
      missing.map(async ({ jobId, attachmentId }) => {
        try {
          const url = await openJobAttachment(jobId, attachmentId);
          return [attachmentId, url || null];
        } catch (err) {
          console.error('Failed to load completion photo preview:', err);
          return [attachmentId, null];
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setAttachmentPreviewUrls((current) => {
        const next = { ...current };
        entries.forEach(([attachmentId, url]) => {
          next[attachmentId] = url;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [attachmentPreviewUrls, jobs, jobsUnlocked]);

  useEffect(() => {
    if (subscriptionLoading) return;

    let mounted = true;
    setLoadingLists(true);

    Promise.all([
      getProperties().then((res) => res?.data || []),
      jobsUnlocked ? listAllTenants().catch(() => []) : Promise.resolve([]),
      getContacts().catch(() => []),
    ])
      .then(([propertyRows, tenantRows, contactRows]) => {
        if (!mounted) return;
        setProperties(Array.isArray(propertyRows) ? propertyRows : []);
        setTenants(Array.isArray(tenantRows) ? tenantRows : []);
        setContacts(Array.isArray(contactRows) ? contactRows : []);
      })
      .catch((err) => {
        console.error('Error loading job form lists:', err);
      })
      .finally(() => {
        if (mounted) setLoadingLists(false);
      });

    return () => {
      mounted = false;
    };
  }, [jobsUnlocked, subscriptionLoading]);

  const contractorContacts = useMemo(() => {
    const preferred = contacts.filter(isContractorContact);
    return preferred.length > 0 ? preferred : contacts.filter(hasValidPhone);
  }, [contacts]);

  const tenantsForProperty = useMemo(() => {
    if (!form.propertyId) return tenants;
    return tenants.filter((tenant) => tenant.propertyId === form.propertyId);
  }, [form.propertyId, tenants]);

  const selectedTenant = tenants.find((tenant) => tenant.id === form.tenantId);
  const selectedFormContractor = contractorContacts.find((contact) => contact.id === form.contractorId);
  const selectedAssignContractor = contractorContacts.find((contact) => contact.id === selectedContractorId);
  const propertyById = useMemo(() => new Map(properties.map((property) => [property.id, property])), [properties]);
  const contactById = useMemo(() => new Map(contacts.map((contact) => [contact.id, contact])), [contacts]);

  const displayedTasks = useMemo(() => {
    return tasks
      .filter((task) => !propertyFilter || task.propertyId === propertyFilter || task.property?.id === propertyFilter)
      .slice()
      .sort((a, b) => {
        if (Boolean(a.completed) !== Boolean(b.completed)) return a.completed ? 1 : -1;
        if (isTaskOverdue(a) !== isTaskOverdue(b)) return isTaskOverdue(a) ? -1 : 1;
        return taskSortValue(a) - taskSortValue(b);
      });
  }, [propertyFilter, tasks]);

  const displayedJobs = useMemo(() => {
    return jobs.filter((job) => !propertyFilter || job.propertyId === propertyFilter || job.property?.id === propertyFilter);
  }, [jobs, propertyFilter]);

  const showTasksSection = viewFilter === 'all' || viewFilter === 'tasks';
  const showJobsSection = viewFilter === 'all' || viewFilter === 'jobs';
  const openTasksCount = displayedTasks.filter((task) => !task.completed).length;
  const overdueTasksCount = displayedTasks.filter(isTaskOverdue).length;
  const activeJobsCount = displayedJobs.filter((job) => !['CLOSED', 'COMPLETED', 'CANCELLED'].includes(job.status)).length;
  const jobsAwaitingActionCount = displayedJobs.filter((job) =>
    ['NEW', 'NEEDS_REASSIGNMENT', 'WORKER_DECLINED', 'QUOTE_SUBMITTED', 'DATE_PROPOSED', 'AWAITING_LANDLORD_CONFIRMATION', 'PROOF_SUBMITTED', 'COMPLETION_SUBMITTED'].includes(job.status) ||
    job.workflow?.nextActor === 'LANDLORD'
  ).length;
  const selectedPropertyName = propertyFilter ? propertyLabel(propertyById.get(propertyFilter)) : '';
  const targetHash = useMemo(() => {
    const raw = String(location.hash || '').replace(/^#/, '');
    if (!raw) return '';
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [location.hash]);

  useEffect(() => {
    if (!targetHash || loading || !jobsUnlocked || displayedJobs.length === 0) return undefined;

    const timer = window.setTimeout(() => {
      const target = document.getElementById(targetHash);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [displayedJobs.length, jobsUnlocked, loading, targetHash]);

  useEffect(() => {
    if (!imageViewer) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setImageViewer(null);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageViewer]);

  const replaceJob = (updatedJob, previousId = null) => {
    if (!updatedJob?.id) return;
    setJobs((current) =>
      current.map((job) =>
        job.id === updatedJob.id || (previousId && job.id === previousId) ? updatedJob : job
      )
    );
  };

  const setActiveAction = (key, label) => {
    setActiveActionLabels((current) => {
      const next = { ...current };
      if (label) next[key] = label;
      else delete next[key];
      return next;
    });
  };

  const handleFormChange = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'propertyId' && current.propertyId !== value) {
        next.tenantId = '';
        next.notifyTenant = false;
      }
      if (field === 'tenantId' && !value) {
        next.notifyTenant = false;
      }
      if (field === 'tenantId' && value && current.accessRequirement === 'LANDLORD_REQUIRED') {
        next.accessRequirement = 'TENANT_REQUIRED';
      }
      return next;
    });
  };

  const openCreateTask = () => {
    setEditingTask(null);
    setTaskForm(emptyTaskForm(propertyFilter || initialPropertyId));
    setTaskFormError('');
    setShowTaskForm(true);
  };

  const openCreateJob = () => {
    setForm(emptyJobForm(propertyFilter || initialPropertyId));
    setCreateError('');
    setShowCreate(true);
  };

  const closeTaskForm = () => {
    if (creatingTask || savingTask) return;
    setShowTaskForm(false);
    setEditingTask(null);
    setTaskFormError('');
  };

  const handleTaskFormChange = (field, value) => {
    setTaskForm((current) => ({ ...current, [field]: value }));
  };

  const taskPayload = () => ({
    title: taskForm.title.trim(),
    description: taskForm.description.trim(),
    dueDate: taskForm.dueDate || null,
    propertyId: taskForm.propertyId || null,
    contactId: taskForm.contactId || null,
  });

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (creatingTask || isActionPending('task:create')) return;
    if (!taskForm.title.trim()) {
      setTaskFormError('Enter a task title.');
      return;
    }

    await runSubmission('task:create', async () => {
      try {
        setCreatingTask(true);
        setTaskFormError('');
        const res = await createTask(taskPayload());
        const created = res?.data;
        if (created) setTasks((current) => [created, ...current.filter((task) => task.id !== created.id)]);
        setTaskForm(emptyTaskForm(propertyFilter || initialPropertyId));
        setShowTaskForm(false);
      } catch (err) {
        console.error('Failed to create task:', err);
        setTaskFormError(err?.response?.data?.error || err?.message || 'Failed to create task.');
      } finally {
        setCreatingTask(false);
      }
    });
  };

  const handleStartEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      dueDate: dateInputValue(task.dueDate),
      propertyId: task.propertyId || task.property?.id || '',
      contactId: task.contactId || task.contact?.id || '',
    });
    setTaskFormError('');
    setShowTaskForm(true);
  };

  const handleSaveTask = async (event) => {
    event.preventDefault();
    if (!editingTask || savingTask || isActionPending(`task:${editingTask.id}:save`)) return;
    if (!taskForm.title.trim()) {
      setTaskFormError('Enter a task title.');
      return;
    }

    await runSubmission(`task:${editingTask.id}:save`, async () => {
      try {
        setSavingTask(true);
        setTaskFormError('');
        const res = await updateTask(editingTask.id, taskPayload());
        const updated = res?.data;
        if (updated) setTasks((current) => current.map((task) => (task.id === updated.id ? updated : task)));
        setShowTaskForm(false);
        setEditingTask(null);
        setTaskFormError('');
      } catch (err) {
        console.error('Failed to update task:', err);
        setTaskFormError(err?.response?.data?.error || err?.message || 'Failed to update task.');
      } finally {
        setSavingTask(false);
      }
    });
  };

  const handleToggleTask = async (task) => {
    await runSubmission(`task:${task.id}:toggle`, async () => {
      try {
        const res = await updateTask(task.id, { completed: !task.completed });
        const updated = res?.data;
        if (updated) setTasks((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } catch (err) {
        console.error('Failed to update task:', err);
        alert('Failed to update task.');
      }
    });
  };

  const handleDeleteTask = async (taskId) => {
    await runSubmission(`task:${taskId}:delete`, async () => {
      if (!window.confirm('Delete this task?')) return;
      try {
        await deleteTask(taskId);
        setTasks((current) => current.filter((task) => task.id !== taskId));
      } catch (err) {
        console.error('Failed to delete task:', err);
        alert('Failed to delete task.');
      }
    });
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();
    if (creatingRef.current || isActionPending('job:create')) return;

    if (!form.propertyId) {
      setCreateError('Choose a property.');
      return;
    }
    if (!form.title.trim()) {
      setCreateError('Enter a job title.');
      return;
    }
    if (!form.accessRequirement) {
      setCreateError('Choose an access requirement.');
      return;
    }

    await runSubmission('job:create', async () => {
      try {
        creatingRef.current = true;
        setCreating(true);
        setCreateError('');
        setCreateNotice('');
        const idempotencyKey = createIdempotencyKey('create-job');
        const created = await createJob({
          ...form,
          title: form.title.trim(),
          description: form.description.trim(),
          tenantAvailability: form.tenantAvailability.trim(),
          accessNotes: form.accessNotes.trim(),
          internalNotes: form.internalNotes.trim(),
          notifyTenant: Boolean(form.notifyTenant && form.tenantId),
          idempotencyKey,
        });

        if (created?.id) {
          const contractorName = contactDisplayName(created.assignedWorker || selectedFormContractor);
          const warnings = Array.isArray(created.warnings) ? created.warnings : [];
          const smsFailed = Boolean(created.assignment?.smsFailureReason) ||
            warnings.some((item) => item.type === 'CONTRACTOR_SMS_FAILED');
          const tenantEmailFailed = warnings.some((item) => item.type === 'TENANT_EMAIL_FAILED');
          const attachmentsFailed = warnings.some((item) => item.type === 'ATTACHMENTS_FAILED');
          setJobs((current) => [created, ...current.filter((job) => job.id !== created.id)]);
          setForm(emptyJobForm(initialPropertyId));
          setShowCreate(false);
          navigate(`/tasks?jobId=${encodeURIComponent(created.id)}`, { replace: true });
          const notices = [];
          if (form.contractorId) {
            notices.push(
              smsFailed
                ? 'Job created, but the contractor SMS could not be sent. Use Retry SMS on the job card.'
                : `Job created and SMS sent to ${contractorName}.`
            );
          } else {
            notices.push('Job created.');
          }
          if (tenantEmailFailed) notices.push('Tenant email could not be sent.');
          if (attachmentsFailed) notices.push('Some attachments could not be uploaded.');
          setCreateNotice(notices.join(' '));
        }
      } catch (err) {
        console.error('Failed to create job:', err);
        const status = Number(err?.response?.status || 500);
        const serverMessage = err?.response?.data?.error;
        setCreateError(
          status >= 400 && status < 500 && serverMessage
            ? serverMessage
            : 'We could not create this job. Please try again.'
        );
      } finally {
        creatingRef.current = false;
        setCreating(false);
      }
    });
  };

  const handleOpenAssignContractor = (job) => {
    setAssigningJob(job);
    setSelectedContractorId(job.assignedWorker?.id || '');
  };

  const handleAssignContractor = async (event) => {
    event.preventDefault();
    const actionKey = assigningJob ? `job:${assigningJob.id}:assign` : 'job:assign';
    if (!assigningJob || assigningJobId === assigningJob.id || isActionPending(actionKey)) return;
    if (!selectedContractorId) {
      alert('Choose a contractor contact first.');
      return;
    }

    await runSubmission(actionKey, async () => {
      try {
        setAssigningJobId(assigningJob.id);
        const updated = await assignJobContractor(assigningJob.id, selectedContractorId, {
          idempotencyKey: createIdempotencyKey('assign-contractor'),
        });
        replaceJob(updated, assigningJob.id);
        setAssigningJob(null);
        setSelectedContractorId('');
        if (updated?.assignment?.smsFailureReason) {
          alert('Contractor assigned, but the SMS could not be sent. Use Retry SMS on the job card.');
        }
      } catch (err) {
        console.error('Failed to assign contractor:', err);
        alert('Failed to assign contractor.');
      } finally {
        setAssigningJobId(null);
      }
    });
  };

  const handleSendSmsInvite = async (job) => {
    const actionKey = `job:${job?.id}:sms`;
    if (isActionPending(actionKey)) return;
    if (!job?.id || !job.assignedWorker?.id) {
      alert('Assign a contractor before sending an SMS invite.');
      return;
    }

    if (job.assignment?.smsSentAt || job.lifecycle?.smsSentAt) {
      alert('SMS invitation already sent.');
      return;
    }

    if (!hasValidPhone(job.assignedWorker)) {
      alert('Assigned contractor needs a valid phone number before an SMS invite can be sent.');
      return;
    }

    await runSubmission(actionKey, async () => {
      try {
        setSendingSmsJobId(job.id);
        const result = await assignJobWorker(job.id, {
          assignedWorkerId: job.assignedWorker.id,
          forceResend: true,
          idempotencyKey: createIdempotencyKey('send-contractor-sms'),
        });

        replaceJob(result?.job);

        if (result?.sms?.sent) {
          alert('SMS invite sent.');
        } else if (result?.sms?.alreadySent) {
          alert('SMS invitation already sent.');
        } else if (result?.sms?.skipped) {
          alert(result.sms.reason || 'SMS invite was not sent.');
        } else if (result?.sms?.reason) {
          alert(result.sms.reason);
        } else {
          alert('SMS invite action completed.');
        }
      } catch (err) {
        console.error('Failed to send SMS invite:', err);
        alert('Failed to send SMS invite.');
      } finally {
        setSendingSmsJobId(null);
      }
    });
  };

  const handleReviewQuote = async (jobId, action) => {
    const actionKey = `job:${jobId}:quote-review`;
    await runSubmission(actionKey, async () => {
      setActiveAction(actionKey, action);
      const notes = action === 'approve' || action === 'accept'
        ? ''
        : window.prompt('Add a note for the contractor') || '';
      try {
        const updated = await reviewJobQuote(jobId, action, notes, {
          idempotencyKey: createIdempotencyKey(`quote-${action}`),
        });
        replaceJob(updated);
      } catch (err) {
        console.error('Failed to update quote:', err);
        alert('Failed to update quote.');
      } finally {
        setActiveAction(actionKey, '');
      }
    });
  };

  const handleQuoteNotRequired = async (jobId) => {
    await runSubmission(`job:${jobId}:quote-required`, async () => {
      const notes = window.prompt('Optional note for why a quote is not required') || '';
      try {
        const updated = await updateJobQuoteRequirement(jobId, false, notes, {
          idempotencyKey: createIdempotencyKey('quote-not-required'),
        });
        replaceJob(updated);
      } catch (err) {
        console.error('Failed to update quote requirement:', err);
        alert('Failed to update quote requirement.');
      }
    });
  };

  const handleScheduleJob = async (job, action = 'confirm') => {
    if (action === 'request_reschedule' || action === 'suggest') {
      const proposed = job.schedule?.proposedDate || job.proposedDate || job.schedule?.scheduledStartAt || '';
      setScheduleModal({
        job,
        date: dateInputValue(proposed),
        time: timeInputValue(proposed),
        notes: '',
        error: '',
      });
      return;
    }

    const actionKey = `job:${job.id}:schedule`;
    await runSubmission(actionKey, async () => {
      setActiveAction(actionKey, action);
      const scheduledDate = job.schedule?.proposedDate || job.proposedDate || job.schedule?.scheduledStartAt || '';
      if (!scheduledDate) {
        alert('No proposed appointment is available to approve.');
        setActiveAction(actionKey, '');
        return;
      }

      try {
        const updated = await scheduleJob(job.id, {
          action,
          scheduledDate,
          scheduledEndAt: job.schedule?.scheduledEndAt || null,
          notes: job.schedule?.notes || '',
          idempotencyKey: createIdempotencyKey(`schedule-${action}`),
        });
        replaceJob(updated);
      } catch (err) {
        console.error('Failed to schedule job:', err);
        alert('Failed to schedule job.');
      } finally {
        setActiveAction(actionKey, '');
      }
    });
  };

  const handleScheduleSuggestionSubmit = async (event) => {
    event.preventDefault();
    if (!scheduleModal?.job?.id) return;

    if (!scheduleModal.date || !scheduleModal.time) {
      setScheduleModal((current) => current ? { ...current, error: 'Choose a date and start time.' } : current);
      return;
    }

    const scheduledDate = buildLocalDateTime(scheduleModal.date, scheduleModal.time);
    const actionKey = `job:${scheduleModal.job.id}:schedule`;
    await runSubmission(actionKey, async () => {
      setActiveAction(actionKey, 'suggest');
      try {
        const updated = await scheduleJob(scheduleModal.job.id, {
          action: 'suggest',
          scheduledDate,
          notes: scheduleModal.notes.trim(),
          idempotencyKey: createIdempotencyKey('schedule-suggest'),
        });
        replaceJob(updated);
        setScheduleModal(null);
      } catch (err) {
        console.error('Failed to suggest appointment:', err);
        setScheduleModal((current) => current ? {
          ...current,
          error: err?.response?.data?.error || 'Failed to submit the suggested time.',
        } : current);
      } finally {
        setActiveAction(actionKey, '');
      }
    });
  };

  const handleReviewProof = async (jobId, action) => {
    const actionKey = `job:${jobId}:proof-review`;
    await runSubmission(actionKey, async () => {
      setActiveAction(actionKey, action);
      const notes = action === 'approve' || action === 'close'
        ? ''
        : window.prompt('Add review notes') || '';
      try {
        const updated = await reviewJobProof(jobId, action, notes, {
          idempotencyKey: createIdempotencyKey(`proof-${action}`),
        });
        replaceJob(updated);
      } catch (err) {
        console.error('Failed to review proof:', err);
        alert('Failed to review proof.');
      } finally {
        setActiveAction(actionKey, '');
      }
    });
  };

  const handleOpenAttachment = async (jobId, attachmentId) => {
    try {
      const url = await openJobAttachment(jobId, attachmentId);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Failed to open attachment:', err);
      alert('Failed to open attachment.');
    }
  };

  const handleOpenCompletionPhoto = async (jobId, attachment) => {
    if (!attachment?.id) return;

    try {
      const url = await openJobAttachment(jobId, attachment.id);
      if (!url) return;

      setAttachmentPreviewUrls((current) => ({
        ...current,
        [attachment.id]: url,
      }));
      setImageViewer({
        url,
        filename: attachment.filename || 'Completion photo',
      });
    } catch (err) {
      console.error('Failed to open completion photo:', err);
      alert('Failed to open completion photo.');
    }
  };

  const renderTaskRow = (task) => {
    const property = task.property || propertyById.get(task.propertyId);
    const contact = task.contact || contactById.get(task.contactId);
    const overdue = isTaskOverdue(task);
    const togglePending = isActionPending(`task:${task.id}:toggle`);
    const deletePending = isActionPending(`task:${task.id}:delete`);

    return (
      <li key={`task-${task.id}`} className="border border-white/10 rounded-lg p-4 bg-white/5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs px-2 py-1 rounded-md bg-sky-500/15 border border-sky-300/20 text-sky-100">
                Task
              </span>
              <span className={`text-xs px-2 py-1 rounded-md border ${
                task.completed
                  ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30'
                  : overdue
                    ? 'bg-red-500/20 text-red-200 border-red-400/30'
                    : 'bg-white/10 text-white/75 border-white/10'
              }`}>
                {task.completed ? 'Complete' : overdue ? 'Overdue' : 'To do'}
              </span>
            </div>
            <p className={`font-semibold text-white ${task.completed ? 'line-through text-white/60' : ''}`}>
              {task.title || 'Untitled task'}
            </p>
            {task.description && <p className="text-sm text-white/70 mt-1">{task.description}</p>}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60">
              <span>Due: {formatDateTime(task.dueDate, 'No due date')}</span>
              <span>Property: {property ? propertyLabel(property) : 'No property'}</span>
              <span>Contact: {contact ? contactDisplayName(contact) : 'No contact'}</span>
            </div>
          </div>

          <div className="flex flex-wrap lg:flex-col gap-2 lg:items-end">
            <button
              type="button"
              onClick={() => handleToggleTask(task)}
              disabled={togglePending}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 text-sm"
              aria-busy={togglePending}
            >
              <CheckCircle2 className="w-4 h-4" />
              {togglePending ? 'Updating...' : task.completed ? 'Mark To Do' : 'Mark Complete'}
            </button>
            <button
              type="button"
              onClick={() => handleStartEditTask(task)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 text-sm"
            >
              <Pencil className="w-4 h-4" />
              Edit Task
            </button>
            <button
              type="button"
              onClick={() => handleDeleteTask(task.id)}
              disabled={deletePending}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-400/20 text-red-200 hover:bg-red-500/20 text-sm disabled:opacity-50"
              aria-busy={deletePending}
            >
              <Trash2 className="w-4 h-4" />
              {deletePending ? 'Deleting...' : 'Delete Task'}
            </button>
          </div>
        </div>
      </li>
    );
  };

  const renderJobRow = (job) => {
    const isLegacyRequest = Boolean(job.isLegacyRequest);
    const quote = job.quote || {};
    const schedule = job.schedule || {};
    const completion = job.completion || {};
    const quoteFiles = quote.attachments || [];
    const completionPhotoFiles = completionPhotoAttachments(job);
    const completionPhotoIds = new Set(completionPhotoFiles.map((attachment) => attachment.id));
    const completionFiles = [
      ...(completion.proofAttachments || []),
      ...(completion.invoiceAttachments || []),
    ].filter((attachment) => !completionPhotoIds.has(attachment.id));
    const generalFiles = (job.attachments || []).filter((attachment) => attachment.type === 'GENERAL');
    const rowId = `job-${job.id}`;
    const quoteSectionId = `quote-${job.id}`;
    const scheduleSectionId = `schedule-${job.id}`;
    const completionEvidenceSectionId = `completion-evidence-${job.id}`;
    const completionSubmittedAt = completion.completionSubmittedAt || completion.proofSubmittedAt;
    const hasCompletionEvidence = Boolean(
      completionSubmittedAt ||
      completion.actualCompletedAt ||
      completion.notes ||
      completion.invoiceReference ||
      completion.invoiceNumber ||
      completion.invoiceAmountGBP != null ||
      completionPhotoFiles.length > 0 ||
      completionFiles.length > 0
    );
    const latestDecline = (job.assignmentHistory || []).find((assignment) => assignment.status === 'DECLINED');
    const smsAlreadySent = Boolean(job.assignment?.smsSentAt || job.lifecycle?.smsSentAt);
    const smsFailed = Boolean(job.assignment?.smsFailureReason);
    const canAssign = isLegacyRequest || !job.assignedWorker?.id || ['NEW', 'NEEDS_REASSIGNMENT', 'WORKER_DECLINED'].includes(job.status);
    const canRetrySms = !isLegacyRequest && job.assignedWorker?.id && !smsAlreadySent && (smsFailed || job.status === 'ASSIGNED');
    const waitingForContractor = !smsFailed && ['ASSIGNED', 'SMS_SENT', 'AWAITING_CONTRACTOR_RESPONSE'].includes(job.status);
    const canMarkNoQuote = !isLegacyRequest && quote.required !== false && ['WORKER_ACCEPTED', 'AWAITING_QUOTE'].includes(job.status);
    const canConfirmAppointment =
      !isLegacyRequest &&
      ['DATE_PROPOSED', 'AWAITING_LANDLORD_CONFIRMATION'].includes(job.status) &&
      job.accessRequirement !== 'TENANT_REQUIRED';
    const waitingForTenant = job.status === 'AWAITING_TENANT_CONFIRMATION';
    const canReviewCompletion = ['PROOF_SUBMITTED', 'COMPLETION_SUBMITTED'].includes(job.status);
    const highlighted = highlightedJobId && highlightedJobId === job.id;
    const assignPending = isActionPending(`job:${job.id}:assign`);
    const smsPending = isActionPending(`job:${job.id}:sms`) || sendingSmsJobId === job.id;
    const quoteReviewPending = isActionPending(`job:${job.id}:quote-review`);
    const quoteRequiredPending = isActionPending(`job:${job.id}:quote-required`);
    const schedulePending = isActionPending(`job:${job.id}:schedule`);
    const proofReviewPending = isActionPending(`job:${job.id}:proof-review`);
    const quoteReviewAction = activeActionLabels[`job:${job.id}:quote-review`] || '';
    const scheduleAction = activeActionLabels[`job:${job.id}:schedule`] || '';
    const proofReviewAction = activeActionLabels[`job:${job.id}:proof-review`] || '';

    return (
      <li
        key={job.id}
        id={rowId}
        tabIndex={targetHash === rowId ? -1 : undefined}
        className={`border rounded-lg p-4 bg-white/5 ${highlighted ? 'border-[#AF9B8A]' : 'border-white/10'}`}
      >
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs px-2 py-1 rounded-md bg-[#AF9B8A]/20 border border-[#AF9B8A]/25 text-[#e8dacb]">
                Maintenance Job
              </span>
              <span className={`px-2 py-1 rounded-md border text-xs ${statusClass(job.status)}`}>
                {formatStatus(job.status)}
              </span>
              <span className="text-xs px-2 py-1 rounded-md bg-[#AF9B8A]/20 text-[#e8dacb]">
                {formatStatus(job.priority, 'Medium')}
              </span>
              <span className="text-xs px-2 py-1 rounded-md bg-white/10 text-white/70">
                {sourceLabel(job)}
              </span>
              <span className="text-xs text-white/50">
                {job.trade || formatStatus(job.jobType, 'Maintenance')}
              </span>
              {job.workflow?.phase && (
                <span className="text-xs px-2 py-1 rounded-md bg-white/10 text-white/70">
                  {job.workflow.phase}
                </span>
              )}
            </div>

            <p className="font-semibold text-white">{job.title}</p>
            <p className="text-sm text-white/70 mt-1">{job.description || 'No description provided.'}</p>
            <p className="text-sm text-white/60 mt-2">
              {job.property?.title || job.property?.address || 'Property'} · {job.tenant?.name || 'No tenant linked'}
            </p>
            <p className="text-sm text-white/60 mt-1">
              Contractor: {job.assignedWorker?.name || job.assignedWorker?.company || 'Unassigned'}
            </p>
            <p className="text-sm text-white/60 mt-1">
              Visit: {formatDateTime(schedule.scheduledStartAt || job.scheduledDate, 'Not scheduled')}
            </p>
            {job.internalNotes && (
              <p className="text-sm text-white/60 mt-2">Internal notes: {job.internalNotes}</p>
            )}
            {job.status === 'NEEDS_REASSIGNMENT' && (
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-red-200">
                <AlertTriangle className="w-4 h-4" />
                Reassignment required{latestDecline?.declineReason ? `: ${latestDecline.declineReason}` : ''}
              </p>
            )}

            {!isLegacyRequest && (
              <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-3 text-sm text-white/70">
                <div id={quoteSectionId} tabIndex={targetHash === quoteSectionId ? -1 : undefined} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-[#AF9B8A] font-medium">Quote</p>
                  <p>{formatStatus(quote.status || 'NOT_SUBMITTED')}</p>
                  {quote.amountGBP != null && <p>GBP {quote.amountGBP}</p>}
                  {quote.notes && <p>{quote.notes}</p>}
                  {quoteFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {quoteFiles.map((attachment) => (
                        <button
                          type="button"
                          key={attachment.id}
                          onClick={() => handleOpenAttachment(job.id, attachment.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 border border-white/10"
                        >
                          <FileText className="w-3 h-3" />
                          {attachment.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div id={scheduleSectionId} tabIndex={targetHash === scheduleSectionId ? -1 : undefined} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-[#AF9B8A] font-medium">Schedule</p>
                  {job.accessRequirement && <p>{formatStatus(job.accessRequirement)}</p>}
                  <p>{formatStatus(schedule.status || 'NOT_STARTED')}</p>
                  <p>
                    {schedule.status === 'PROPOSED' ? 'Proposed appointment: ' : 'Visit: '}
                    {formatDateTime(schedule.proposedDate || schedule.scheduledStartAt || job.proposedDate || job.scheduledDate, 'Not scheduled')}
                  </p>
                  {schedule.notes && <p>{schedule.notes}</p>}
                  {schedule.tenantResponse && <p>Tenant: {formatStatus(schedule.tenantResponse)}</p>}
                  {schedule.appointmentRejectionReason && <p>Rejected: {schedule.appointmentRejectionReason}</p>}
                </div>
                <div
                  id={completionEvidenceSectionId}
                  tabIndex={targetHash === completionEvidenceSectionId ? -1 : undefined}
                  className={`bg-white/5 border rounded-lg p-3 ${
                    targetHash === completionEvidenceSectionId ? 'border-[#AF9B8A]' : 'border-white/10'
                  }`}
                >
                  <p className="text-[#AF9B8A] font-medium">Completion</p>
                  <p>{formatStatus(completion.reviewStatus || 'NOT_SUBMITTED')}</p>
                  {hasCompletionEvidence && (
                    <section className="mt-3 border-t border-white/10 pt-3">
                      <div className="flex flex-col gap-1">
                        <p className="text-white font-semibold">Completion Evidence</p>
                        {completion.actualCompletedAt && (
                          <p>Completed: {formatDateTime(completion.actualCompletedAt, '')}</p>
                        )}
                        {completionSubmittedAt && (
                          <p>Submitted: {formatDateTime(completionSubmittedAt, '')}</p>
                        )}
                        {completion.notes && <p>{completion.notes}</p>}
                        {completion.invoiceReference && <p>Invoice reference: {completion.invoiceReference}</p>}
                        {completion.invoiceNumber && <p>Invoice: {completion.invoiceNumber}</p>}
                        {completion.invoiceAmountGBP != null && <p>Invoice amount: GBP {completion.invoiceAmountGBP}</p>}
                      </div>

                      {completionPhotoFiles.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[#AF9B8A] font-medium">Photos</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {completionPhotoFiles.map((attachment) => {
                          const previewUrl = attachmentPreviewUrls[attachment.id];
                          const label = `Open completion photo ${attachment.filename || ''}`.trim();
                          const thumbnail = previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={attachment.filename || 'Completion photo'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center px-2 text-xs text-white/60">
                              Photo
                            </span>
                          );

                          return (
                            <button
                              type="button"
                              key={attachment.id}
                              onClick={() => handleOpenCompletionPhoto(job.id, attachment)}
                              className="relative aspect-square rounded-lg overflow-hidden bg-white/10 border border-white/10 text-left hover:border-[#AF9B8A]/70 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                              aria-label={label}
                            >
                              {thumbnail}
                            </button>
                          );
                        })}
                      </div>
                        </div>
                      )}

                      {completionFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {completionFiles.map((attachment) => (
                            <button
                              type="button"
                              key={attachment.id}
                              onClick={() => handleOpenAttachment(job.id, attachment.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20"
                            >
                              <FileText className="w-3 h-3" />
                              {attachment.filename}
                            </button>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </div>
            )}

            {generalFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {generalFiles.map((attachment) => (
                  <button
                    type="button"
                    key={attachment.id}
                    onClick={() => handleOpenAttachment(job.id, attachment.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 border border-white/10 text-sm text-white/70"
                  >
                    <Paperclip className="w-3 h-3" />
                    {attachment.filename}
                  </button>
                ))}
              </div>
            )}

            {!isLegacyRequest && job.activities?.length > 0 && (
              <details className="mt-3 text-sm text-white/60">
                <summary className="cursor-pointer text-white/80">Timeline</summary>
                <ul className="mt-2 space-y-2">
                  {job.activities.slice(0, 8).map((activity) => (
                    <li key={activity.id || `${activity.type}-${activity.createdAt}`}>
                      <span className="text-white">{activity.title}</span>
                      {activity.description ? ` - ${activity.description}` : ''}
                      <span className="block text-xs text-white/40">
                        {formatDateTime(activity.createdAt, '')}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className="flex flex-col gap-2 lg:items-end min-w-[210px]">
            {canAssign && (
              <button
                type="button"
                onClick={() => handleOpenAssignContractor(job)}
                disabled={assignPending}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#AF9B8A] hover:bg-[#AF9B8A]/80 text-sm disabled:opacity-50"
                aria-busy={assignPending}
              >
                <UserPlus className="w-4 h-4" />
                {assignPending ? 'Assigning...' : job.assignedWorker?.id ? 'Reassign Contractor' : 'Assign Contractor'}
              </button>
            )}

            {canRetrySms && (
              <button
                type="button"
                onClick={() => handleSendSmsInvite(job)}
                disabled={smsPending}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                aria-busy={smsPending}
              >
                <Send className="w-4 h-4" />
                {smsPending ? 'Sending...' : smsFailed ? 'Retry SMS' : 'Send SMS'}
              </button>
            )}

            {!isLegacyRequest && job.status === 'QUOTE_SUBMITTED' && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleReviewQuote(job.id, 'approve')}
                  disabled={quoteReviewPending}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-sm"
                  aria-busy={quoteReviewPending}
                >
                  {quoteReviewAction === 'approve' ? 'Approving...' : 'Approve Quote'}
                </button>
                <button
                  type="button"
                  onClick={() => handleReviewQuote(job.id, 'reject')}
                  disabled={quoteReviewPending}
                  className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/30 text-sm disabled:opacity-50"
                  aria-busy={quoteReviewPending}
                >
                  {quoteReviewAction === 'reject' ? 'Declining...' : 'Decline Quote'}
                </button>
                <button
                  type="button"
                  onClick={() => handleReviewQuote(job.id, 'request_revision')}
                  disabled={quoteReviewPending}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm disabled:opacity-50"
                  aria-busy={quoteReviewPending}
                >
                  {quoteReviewAction === 'request_revision' ? 'Submitting...' : 'Request Revision'}
                </button>
              </div>
            )}

            {canMarkNoQuote && (
              <button
                type="button"
                onClick={() => handleQuoteNotRequired(job.id)}
                disabled={quoteRequiredPending}
                className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm disabled:opacity-50"
                aria-busy={quoteRequiredPending}
              >
                {quoteRequiredPending ? 'Saving...' : 'No Quote Required'}
              </button>
            )}

            {canConfirmAppointment && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleScheduleJob(job, 'confirm')}
                  disabled={schedulePending}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-sm"
                  aria-busy={schedulePending}
                >
                  <CalendarClock className="w-4 h-4" />
                  {scheduleAction === 'confirm' ? 'Approving...' : 'Approve Appointment'}
                </button>
                <button
                  type="button"
                  onClick={() => handleScheduleJob(job, 'request_reschedule')}
                  disabled={schedulePending}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm disabled:opacity-50"
                  aria-busy={schedulePending}
                >
                  {scheduleAction === 'suggest' ? 'Submitting...' : 'Suggest Different Time'}
                </button>
              </div>
            )}

            {canReviewCompletion && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleReviewProof(job.id, 'approve')}
                  disabled={proofReviewPending}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-sm"
                  aria-busy={proofReviewPending}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {proofReviewAction === 'approve' ? 'Approving...' : 'Approve Work'}
                </button>
                <button
                  type="button"
                  onClick={() => handleReviewProof(job.id, 'follow_up')}
                  disabled={proofReviewPending}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm"
                  aria-busy={proofReviewPending}
                >
                  <RotateCcw className="w-4 h-4" />
                  {proofReviewAction === 'follow_up' ? 'Submitting...' : 'Request Follow Up'}
                </button>
              </div>
            )}

            {!isLegacyRequest && job.status === 'LANDLORD_APPROVED' && (
              <button
                type="button"
                onClick={() => handleReviewProof(job.id, 'close')}
                disabled={proofReviewPending}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-sm"
                aria-busy={proofReviewPending}
              >
                <CheckCircle2 className="w-4 h-4" />
                {proofReviewAction === 'close' ? 'Completing...' : 'Mark Complete'}
              </button>
            )}

            {!canAssign && !canRetrySms && !canMarkNoQuote && !canConfirmAppointment && !canReviewCompletion && job.status !== 'QUOTE_SUBMITTED' && job.status !== 'LANDLORD_APPROVED' && (
              <div className="text-xs text-white/60 bg-white/5 border border-white/10 rounded-lg px-3 py-2 max-w-[240px]">
                {waitingForTenant
                  ? 'Waiting for tenant confirmation.'
                  : waitingForContractor
                    ? 'Waiting for contractor response.'
                    : job.workflow?.nextActor
                      ? `Next: ${formatStatus(job.workflow.nextActor)}`
                      : 'No landlord action needed.'}
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="pt-10 md:pt-0">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl">Tasks & Jobs</h1>
          <p className="text-white/60 mt-2">
            General landlord tasks stay separate from maintenance jobs and contractor workflows.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={openCreateTask}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/10 text-white font-semibold hover:bg-[#AF9B8A]/20 transition"
          >
            <ClipboardList className="w-4 h-4" />
            Create Task
          </button>
          <button
            type="button"
            onClick={openCreateJob}
            disabled={!jobsUnlocked}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Briefcase className="w-4 h-4" />
            Create Job
          </button>
        </div>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      <div className="glass p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {[
              ['all', 'All'],
              ['tasks', 'Tasks'],
              ['jobs', 'Jobs'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setViewFilter(value)}
                className={`px-3 py-2 rounded-lg border text-sm transition ${
                  viewFilter === value
                    ? 'bg-[#AF9B8A] border-[#AF9B8A] text-white'
                    : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-white/70">
            <span>Property</span>
            <select
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              className="min-w-0 sm:min-w-[220px] p-2 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            >
              <option value="" className="text-black">All properties</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id} className="text-black">
                  {propertyLabel(property)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {selectedPropertyName && (
          <p className="mt-3 text-xs text-white/50">
            Showing tasks and jobs linked to {selectedPropertyName}.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        {[
          ['Open Tasks', openTasksCount],
          ['Overdue Tasks', overdueTasksCount],
          ['Active Jobs', activeJobsCount],
          ['Jobs Awaiting Action', jobsAwaitingActionCount],
        ].map(([label, value]) => (
          <div key={label} className="glass p-4">
            <p className="text-xs uppercase text-white/50">{label}</p>
            <p className="text-3xl text-[#AF9B8A] mt-1">{value}</p>
          </div>
        ))}
      </div>

      {showTasksSection && !tasksLoading && !tasksError && displayedTasks.length === 0 && showJobsSection && !loading && !error && displayedJobs.length === 0 && (
        <div className="glass p-6 mb-6">
          <p className="text-xl font-semibold">No tasks or jobs yet</p>
          <p className="text-white/60 mt-2">Create a general task for admin work, or create a maintenance job when a contractor workflow is needed.</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button type="button" onClick={openCreateTask} className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20">
              Create Task
            </button>
            <button type="button" onClick={openCreateJob} disabled={!jobsUnlocked} className="px-4 py-2 rounded-lg bg-[#AF9B8A] hover:bg-[#AF9B8A]/80 disabled:opacity-50">
              Create Job
            </button>
          </div>
        </div>
      )}

      {showTasksSection && (
        <section className="glass p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-[#AF9B8A]" />
              <h2 className="text-2xl font-bold">General Tasks</h2>
            </div>
            <button
              type="button"
              onClick={openCreateTask}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-[#AF9B8A]/20 text-sm"
            >
              Create Task
            </button>
          </div>

          {tasksLoading && <p className="text-sm text-white/70">Loading tasks...</p>}
          {tasksError && <p className="text-sm text-red-300">{tasksError}</p>}
          {!tasksLoading && !tasksError && displayedTasks.length === 0 && (
            <div className="border border-white/10 rounded-lg p-4 bg-white/5 text-white/70">
              No general tasks found.
            </div>
          )}
          <ul className="space-y-3">
            {displayedTasks.map(renderTaskRow)}
          </ul>
        </section>
      )}

      {showJobsSection && (
        <section className="glass p-6 relative overflow-hidden">
          <div className={jobsUnlocked ? '' : 'blur-sm pointer-events-none select-none'}>
            {createNotice && (
              <div className="mb-4 border border-[#AF9B8A]/30 bg-[#AF9B8A]/10 rounded-lg p-3 text-sm text-[#e8dacb]">
                {createNotice}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-[#AF9B8A]" />
                <h2 className="text-2xl font-bold">Maintenance Jobs</h2>
              </div>
              <button
                type="button"
                onClick={openCreateJob}
                disabled={!jobsUnlocked}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#AF9B8A] hover:bg-[#AF9B8A]/80 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Job
              </button>
            </div>

            {loading && <p className="text-sm text-white/70">Loading jobs...</p>}
            {error && <p className="text-sm text-red-300">{error}</p>}

            {!loading && !error && displayedJobs.length === 0 && (
              <div className="border border-white/10 rounded-lg p-4 bg-white/5 text-white/70">
                No maintenance jobs found.
              </div>
            )}

            <ul className="space-y-3">
              {displayedJobs.map(renderJobRow)}
            </ul>
          </div>

          {!subscriptionLoading && !jobsUnlocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/45 p-6">
              <div className="max-w-md text-center bg-[#23272f] border border-white/10 rounded-lg p-6 shadow-xl">
                <p className="text-xl font-semibold mb-2">Jobs are a Beem Enhanced feature</p>
                <p className="text-sm text-white/70 mb-4">
                  Assign contractors, review quotes, schedule visits and approve proof of work.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  className="px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition"
                >
                  Upgrade
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {showTaskForm && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-4 pb-8 overflow-y-auto">
          <form
            onSubmit={editingTask ? handleSaveTask : handleCreateTask}
            className="bg-[#23272f] rounded-lg shadow-lg w-full max-w-2xl mx-auto relative p-6 max-h-[calc(100vh-2rem)] overflow-y-auto"
          >
            <button
              type="button"
              onClick={closeTaskForm}
              className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
              aria-label="Close task form"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold mb-5 text-white">
              {editingTask ? 'Edit Task' : 'Create Task'}
            </h2>
            {taskFormError && <p className="mb-4 text-sm text-red-300">{taskFormError}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Title</span>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => handleTaskFormChange('title', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Call tenant"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Description</span>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => handleTaskFormChange('description', e.target.value)}
                  rows={3}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Optional notes"
                />
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Due Date</span>
                <input
                  type="date"
                  value={taskForm.dueDate}
                  onChange={(e) => handleTaskFormChange('dueDate', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                />
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Property</span>
                <select
                  value={taskForm.propertyId}
                  onChange={(e) => handleTaskFormChange('propertyId', e.target.value)}
                  disabled={Boolean(initialPropertyId)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass disabled:opacity-60"
                >
                  <option value="" className="text-black">No property</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id} className="text-black">
                      {propertyLabel(property)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Contact</span>
                <select
                  value={taskForm.contactId}
                  onChange={(e) => handleTaskFormChange('contactId', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                >
                  <option value="" className="text-black">No contact</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id} className="text-black">
                      {contactDisplayName(contact)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={closeTaskForm}
                disabled={creatingTask || savingTask}
                className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingTask || savingTask}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingTask || savingTask
                  ? editingTask
                    ? 'Saving...'
                    : 'Creating...'
                  : editingTask
                    ? 'Save Task'
                    : 'Create Task'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-4 pb-8 overflow-y-auto">
          <form
            onSubmit={handleCreateJob}
            className="bg-[#23272f] rounded-lg shadow-lg w-full max-w-4xl mx-auto relative p-6 max-h-[calc(100vh-2rem)] overflow-y-auto"
          >
            <button
              type="button"
              onClick={() => {
                if (!creating) setShowCreate(false);
              }}
              className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
              aria-label="Close create job form"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold mb-5 text-white">Create Job</h2>
            {createError && <p className="mb-4 text-sm text-red-300">{createError}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Property</span>
                <select
                  value={form.propertyId}
                  onChange={(e) => handleFormChange('propertyId', e.target.value)}
                  disabled={loadingLists || Boolean(initialPropertyId)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass disabled:opacity-60"
                >
                  <option value="" className="text-black">
                    {loadingLists ? 'Loading properties...' : 'Select property'}
                  </option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id} className="text-black">
                      {propertyLabel(property)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Priority</span>
                <select
                  value={form.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority} className="text-black">
                      {formatStatus(priority)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Title</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Boiler service"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Description</span>
                <textarea
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={4}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Describe the job"
                />
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Category</span>
                <select
                  value={form.jobType}
                  onChange={(e) => handleFormChange('jobType', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                >
                  {JOB_TYPES.map((type) => (
                    <option key={type} value={type} className="text-black">
                      {formatStatus(type)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Access</span>
                <select
                  value={form.accessRequirement}
                  onChange={(e) => handleFormChange('accessRequirement', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                >
                  {ACCESS_REQUIREMENTS.map((access) => (
                    <option key={access} value={access} className="text-black">
                      {formatStatus(access)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Tenant</span>
                <select
                  value={form.tenantId}
                  onChange={(e) => handleFormChange('tenantId', e.target.value)}
                  disabled={!form.propertyId}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass disabled:opacity-60"
                >
                  <option value="" className="text-black">No tenant linked</option>
                  {tenantsForProperty.map((tenant) => (
                    <option key={tenant.id} value={tenant.id} className="text-black">
                      {tenant.name || tenant.email || 'Tenant'}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Contractor</span>
                <select
                  value={form.contractorId}
                  onChange={(e) => handleFormChange('contractorId', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                >
                  <option value="" className="text-black">Unassigned</option>
                  {contractorContacts.map((contact) => (
                    <option key={contact.id} value={contact.id} className="text-black">
                      {contactDisplayName(contact)}
                      {contact.phone ? ` - ${contact.phone}` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-white/55">
                  {selectedFormContractor
                    ? `Assigning ${contactDisplayName(selectedFormContractor)} will automatically send them an SMS with a secure link to this job.`
                    : 'No contractor SMS will be sent while Contractor is Unassigned.'}
                </p>
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Preferred Timing</span>
                <input
                  type="text"
                  value={form.tenantAvailability}
                  onChange={(e) => handleFormChange('tenantAvailability', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Weekday mornings"
                />
              </label>

              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Access Notes</span>
                <input
                  type="text"
                  value={form.accessNotes}
                  onChange={(e) => handleFormChange('accessNotes', e.target.value)}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Keys held by agent"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Internal Notes</span>
                <textarea
                  value={form.internalNotes}
                  onChange={(e) => handleFormChange('internalNotes', e.target.value)}
                  rows={3}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Private landlord notes"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Attachments</span>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFormChange('attachments', Array.from(e.target.files || []))}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                />
              </label>
            </div>

            <label className="mt-4 flex items-center gap-3 text-sm text-white/80">
              <input
                type="checkbox"
                checked={Boolean(form.notifyTenant)}
                disabled={!selectedTenant}
                onChange={(e) => handleFormChange('notifyTenant', e.target.checked)}
                className="appearance-none w-5 h-5 border-2 border-[#AF9B8A] rounded-md checked:bg-[#AF9B8A] checked:border-[#AF9B8A] focus:ring-2 focus:ring-[#AF9B8A] cursor-pointer transition disabled:opacity-50"
              />
              Notify tenant about this job
            </label>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create Job'}
              </button>
            </div>
          </form>
        </div>
      )}

      {scheduleModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-4 pb-8 overflow-y-auto">
          <form
            onSubmit={handleScheduleSuggestionSubmit}
            className="bg-[#23272f] rounded-lg shadow-lg w-full max-w-lg mx-auto relative p-6"
            aria-busy={isActionPending(`job:${scheduleModal.job.id}:schedule`)}
          >
            <button
              type="button"
              onClick={() => {
                if (!isActionPending(`job:${scheduleModal.job.id}:schedule`)) setScheduleModal(null);
              }}
              className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
              aria-label="Close scheduling form"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold mb-2 text-white">Suggest Different Time</h2>
            <p className="text-sm text-white/60 mb-5">
              Current proposal: {formatDateTime(
                scheduleModal.job.schedule?.proposedDate ||
                  scheduleModal.job.proposedDate ||
                  scheduleModal.job.schedule?.scheduledStartAt,
                'Not set'
              )}
            </p>
            {scheduleModal.error && <p className="mb-4 text-sm text-red-300">{scheduleModal.error}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Date</span>
                <input
                  type="date"
                  value={scheduleModal.date}
                  onChange={(e) => setScheduleModal((current) => ({ ...current, date: e.target.value, error: '' }))}
                  required
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-white/70 mb-1">Start time</span>
                <input
                  type="time"
                  value={scheduleModal.time}
                  onChange={(e) => setScheduleModal((current) => ({ ...current, time: e.target.value, error: '' }))}
                  required
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="block text-sm text-white/70 mb-1">Scheduling note</span>
                <textarea
                  value={scheduleModal.notes}
                  onChange={(e) => setScheduleModal((current) => ({ ...current, notes: e.target.value }))}
                  rows={3}
                  className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                  placeholder="Optional reason or access note"
                />
              </label>
            </div>

            {scheduleModal.date && scheduleModal.time && (
              <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-3">
                <p className="text-sm text-white/60">Suggested appointment</p>
                <p className="text-white mt-1">
                  {formatDateTime(buildLocalDateTime(scheduleModal.date, scheduleModal.time), 'Not set')}
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setScheduleModal(null)}
                disabled={isActionPending(`job:${scheduleModal.job.id}:schedule`)}
                className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isActionPending(`job:${scheduleModal.job.id}:schedule`)}
                className="px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isActionPending(`job:${scheduleModal.job.id}:schedule`) ? 'Submitting...' : 'Submit Time'}
              </button>
            </div>
          </form>
        </div>
      )}

      {assigningJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <form
            onSubmit={handleAssignContractor}
            className="bg-[#23272f] rounded-lg shadow-lg w-full max-w-lg mx-auto relative p-6"
          >
            <button
              type="button"
              onClick={() => {
                if (assigningJobId !== assigningJob.id) setAssigningJob(null);
              }}
              className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
              aria-label="Close assign contractor form"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold mb-4 text-white">Assign Contractor</h2>
            <select
              value={selectedContractorId}
              onChange={(e) => setSelectedContractorId(e.target.value)}
              className="w-full p-3 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            >
              <option value="" className="text-black">Select contractor</option>
              {contractorContacts.map((contact) => (
                <option key={contact.id} value={contact.id} className="text-black">
                  {contactDisplayName(contact)}
                  {contact.relationship ? ` - ${contact.relationship}` : ''}
                  {contact.phone ? ` - ${contact.phone}` : ''}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-white/55">
              {selectedAssignContractor
                ? `Assigning ${contactDisplayName(selectedAssignContractor)} will automatically send them an SMS with a secure link to this job.`
                : 'Choose a contractor to send them an SMS with a secure job link.'}
            </p>
            <button
              type="submit"
              disabled={!selectedContractorId || assigningJobId === assigningJob.id || isActionPending(`job:${assigningJob.id}:assign`)}
              className="mt-5 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-busy={assigningJobId === assigningJob.id || isActionPending(`job:${assigningJob.id}:assign`)}
            >
              {assigningJobId === assigningJob.id || isActionPending(`job:${assigningJob.id}:assign`) ? 'Assigning...' : 'Assign Contractor'}
            </button>
          </form>
        </div>
      )}

      {imageViewer && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setImageViewer(null)}
        >
          <div
            className="relative flex max-h-full w-full max-w-5xl flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setImageViewer(null)}
              className="absolute right-2 top-2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white hover:bg-[#AF9B8A]"
              aria-label="Close image viewer"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={imageViewer.url}
              alt={imageViewer.filename || 'Completion evidence'}
              className="max-h-[85vh] w-full object-contain rounded-lg bg-black"
            />
            {imageViewer.filename && (
              <p className="mt-3 text-center text-sm text-white/70">{imageViewer.filename}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
